import Box from "@mui/material/Box";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SwipeableViews from "react-swipeable-views";
import { useTheme } from "@mui/material/styles";

import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import GetAppIcon from "@mui/icons-material/GetApp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import RoomIcon from "@mui/icons-material/Room";
import {
  actionInfoBuildingIndexUpdated,
  actionInfoZoomAndHighlight,
} from "./InfoRedux";
import BuildingActions from "./BuildingActions";
import Tooltip from "@mui/material/Tooltip";
import { floorSorter } from "../AppBar/Floor";
import { actionAppBarFloorUpdated } from "../AppBar";
import PDFDownloadDialog from "./PDFDownloadDialog";
import DownloadDialog from "./DownloadDialog";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import { CopyToClipboard } from "react-copy-to-clipboard";

const root_ = (theme) => ({
  backgroundColor: theme.palette.background.paper,
  width: "100%",
});

const tabs_ = (theme) => ({
  "& button.MuiTab-root": {
    minWidth: "auto !important",
  },
});

// Avoids a "Text is clipped when resized" complaint from Siteimprove.
const tab_ = (theme) => ({
  overflow: "auto",
});

export default function BuildingInfo({ building }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const tabIndex = useSelector((state) => state.infoReducer.buildingInfoIndex);
  const handleChange = (event, newValue) => {
    dispatch(actionInfoBuildingIndexUpdated(newValue));
  };
  const handleChangeIndex = (index) => {
    dispatch(actionInfoBuildingIndexUpdated(index));
  };

  return (
    <Box sx={root_}>
      <AppBar position="static" color="default">
        <Tabs
          value={tabIndex}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          aria-label="map layer tabs"
          sx={tabs_}
        >
          <Tab
            label="BASIC"
            sx={tab_}
            {...a11yProps(0)}
            data-testid="info-building-basic-tab"
          />
          <Tab
            label="ADDITIONAL"
            sx={tab_}
            {...a11yProps(1)}
            data-testid="info-building-additional-tab"
          />
        </Tabs>
      </AppBar>
      <SwipeableViews
        axis={theme.direction === "rtl" ? "x-reverse" : "x"}
        index={tabIndex}
        onChangeIndex={handleChangeIndex}
      >
        <TabPanel value={tabIndex} index={0} dir={theme.direction}>
          <BasicInfo building={building} />
        </TabPanel>
        <TabPanel value={tabIndex} index={1} dir={theme.direction}>
          <Additional building={building} />
        </TabPanel>
      </SwipeableViews>
    </Box>
  );
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Typography
      component="div"
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && <Box style={{ padding: "16px" }}>{children}</Box>}
    </Typography>
  );
}

function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    "aria-controls": `full-width-tabpanel-${index}`,
  };
}

const root2_ = (theme) => ({
  "& .MuiTypography-overline": {
    lineHeight: 1.25,
    letterSpacing: "0.03em",
  },
});

const title_ = (theme) => ({
  fontSize: "1.5rem",
  fontWeight: "bold",
  marginBottom: "4px",
  letterSpacing: "0 !important",
});

const type_ = (theme) => ({
  marginLeft: "2px",
  fontSize: "0.65rem",
  letterSpacing: "0.01em !important",
  fontWeight: "bold",
  color: "rgb(195, 88, 135)",
});

const typeIcon_ = (theme) => ({
  color: "white !important",
  backgroundColor: "rgb(195, 88, 135)",
  borderRadius: "50%",
  letterSpacing: "0",
  display: "inline-block",
  width: 18,
  height: 18,
  fontSize: "0.55rem",
  lineHeight: "18px",
  textAlign: "center",
  marginLeft: "4px",
});

const stack_ = (theme) => ({
  display: "inline-block",
  "& .MuiButton-root": {
    lineHeight: 1,
  },
  "& .MuiTypography-overline": {
    lineHeight: 1,
  },
});

const secondary_ = (theme) => ({
  color: "grey",
  fontSize: "0.6rem",
  letterSpacing: "0.02em !important",
});

const table_ = (theme) => ({
  marginBottom: "16px",
  lineHeight: 1.1,
});

const tableRow_ = (theme) => ({
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.025)",
  },
});

const actionIcon_ = (theme) => ({
  padding: "2px",
  marginRight: "2px",
  marginTop: "-3px",
  fontSize: "1.25rem",
  "& .MuiSvgIcon-root": {
    fontSize: "1.25rem",
  },
});

const floorRoot_ = (theme) => ({
  lineHeight: 0,
});

const floor_ = (theme) => ({
  fontWeight: "bold",
  marginRight: "4px",
});

const floorBtn_ = (theme) => ({
  fontSize: "0.65rem",
  padding: "2px 4px",
  minWidth: 24,
  minHeight: 24,
  lineHeight: 1,
  color: "grey",
  "& .MuiSvgIcon-root": {
    fontSize: "1rem",
    marginRight: "4px",
    color: "#5a5a5a",
  },
});

function BasicInfo({ building }) {
  const dispatch = useDispatch();

  const {
    bld_descrshort,
    bldrecnbr,
    bld_descr50,
    bldstreetnbr,
    bldstreetname,
    bldcity,
    bldstate,
    bldpostal,
    bldtyp_descr,
    bldcampuscd_descr,
  } = building;

  const streetLabel = bldstreetnbr + " " + bldstreetname;
  const defaultBrnTooltip = "Building Record Number";
  const [bldrecnbrLabel, setBldrecnbrLabel] = useState(defaultBrnTooltip);
  const onCopyHandler = () => {
    setBldrecnbrLabel(`${defaultBrnTooltip} Copied to Clipboard`);
    setTimeout(() => {
      setBldrecnbrLabel(defaultBrnTooltip);
    }, 1500);
  };
  const zoomTo = () => {
    dispatch(actionInfoZoomAndHighlight(bldrecnbr));
  };
  const googleMapsUrl = `https://maps.google.com/?q=${bldstreetnbr} ${bldstreetname}, ${bldcity}, ${bldstate} ${bldpostal}`;

  return (
    <Box sx={root2_}>
      <Box>
        <Tooltip describeChild title={bldrecnbrLabel}>
          <span>
            <CopyToClipboard text={bldrecnbr} onCopy={onCopyHandler}>
              <Typography
                component="span"
                variant="overline"
                data-testid="info-building-bldrecnbr"
              >
                {bldrecnbr}
              </Typography>
            </CopyToClipboard>
          </span>
        </Tooltip>
        <span> </span>{" "}
        {/* fix issue when double clicking bldrecnbr grabs TYP too */}
        <Box component="span" sx={typeIcon_}>
          TYP
        </Box>
        <Tooltip describeChild title="Building type">
          <Typography
            component="span"
            variant="overline"
            sx={type_}
            data-testid="info-building-type"
          >
            {bldtyp_descr}
          </Typography>
        </Tooltip>
      </Box>
      <Box style={{ marginTop: "8px" }}>
        <Typography
          component="span"
          variant="overline"
          sx={title_}
          data-testid="info-building-name"
        >
          {bld_descrshort}
          <Tooltip title="Zoom In">
            <IconButton
              color="primary"
              onClick={zoomTo}
              sx={actionIcon_}
              size="large"
            >
              <RoomIcon />
            </IconButton>
          </Tooltip>
        </Typography>
        <Box sx={stack_}>
          <Typography
            component="div"
            variant="overline"
            sx={secondary_}
            data-testid="info-building-campus"
          >
            {bldcampuscd_descr}
          </Typography>
          <Typography component="div" variant="overline" sx={secondary_}>
            <Tooltip title="Open Address in Google Maps">
              <a
                aria-label={streetLabel}
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="info-building-address"
              >
                {streetLabel}
              </a>
            </Tooltip>
          </Typography>
        </Box>
      </Box>
      <Typography
        component="div"
        variant="overline"
        sx={secondary_}
        style={{ marginTop: "-4px", marginBottom: "12px" }}
      >
        {bld_descr50}
      </Typography>
      <CompactFloors building={building} />
      <BuildingActions building={building} />
    </Box>
  );
}

function CompactFloors({ building }) {
  const dispatch = useDispatch();

  const { floor_list, bldrecnbr, bld_descrshort } = building;

  const [anchorEl, setAnchorEl] = useState(null);
  const [activeFloor, setActiveFloor] = useState(null);

  const handleClick = (floor) => (event) => {
    setActiveFloor(floor);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const zoomToFloor = () => {
    dispatch(actionInfoZoomAndHighlight(bldrecnbr, activeFloor));
    dispatch(actionAppBarFloorUpdated(activeFloor));
    handleClose();
  };

  const [downloadVisible, setDownloadVisible] = useState(false);
  const handleDownloadClick = () => {
    setDownloadVisible(true);
    handleClose();
  };
  const handleDownloadClose = () => {
    setDownloadVisible(false);
  };

  const [pdfDownloadVisible, setPdfDownloadVisible] = useState(false);
  const handlePdfDownloadClick = () => {
    setPdfDownloadVisible(true);
    handleClose();
  };
  const handlePdfDownloadClose = () => {
    setPdfDownloadVisible(false);
  };

  const tempFloors = floor_list ? [...new Set(floor_list.split(", "))] : [];
  const floors = tempFloors.sort(floorSorter).reverse();

  return floors.length > 0 ? (
    <Box sx={floorRoot_}>
      <Typography component="span" variant="overline" sx={floor_}>
        Floor
      </Typography>
      <span>
        {floors.map((floor) => {
          return (
            <Button
              key={floor}
              onClick={handleClick(floor)}
              size="small"
              sx={floorBtn_}
              style={{ color: "#3f51b5" }}
            >
              {floor}
            </Button>
          );
        })}
      </span>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={handleClose} disabled={true}>
          <Typography variant="overline">
            <strong>{`FLOOR ${activeFloor}`}</strong>
          </Typography>
        </MenuItem>
        <MenuItem onClick={zoomToFloor}>
          <ListItemIcon>
            <RoomIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="overline">Zoom</Typography>
        </MenuItem>
        <MenuItem onClick={handleDownloadClick}>
          <ListItemIcon>
            <GetAppIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="overline">Data</Typography>
        </MenuItem>
        <MenuItem onClick={handlePdfDownloadClick}>
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="overline">Floor Plan</Typography>
        </MenuItem>
      </Menu>
      <PDFDownloadDialog
        open={pdfDownloadVisible}
        onClose={handlePdfDownloadClose}
        bldrecnbr={bldrecnbr}
        name={bld_descrshort}
        floor={activeFloor}
      />
      <DownloadDialog
        open={downloadVisible}
        onClose={handleDownloadClose}
        bldrecnbr={bldrecnbr}
        floor={activeFloor}
        name={bld_descrshort}
      />
    </Box>
  ) : (
    ""
  );
}

function Additional({ building }) {
  const {
    bldgrsssqrft,
    bldcnstsqrft,
    netsqrft,
    assignedsqrft,
    nonassignedsqrft,
    circsqrft,
    bldphase_descrshort,
    bldownership_descr,
    bldsuit_descrshort,
    bldstreetnbr,
    bldstreetname,
    bldcity,
    bldstate,
    bldpostal,
  } = building;

  return (
    <Box sx={root2_}>
      <Grid container spacing={0} sx={table_}>
        <Grid container size={{ xs: 12 }} sx={tableRow_}>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">Gross Area</Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">{`${numberWithCommas(bldgrsssqrft)} sqft`}</Typography>
          </Grid>
        </Grid>
        <Grid container size={{ xs: 12 }} sx={tableRow_}>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">Building Construction</Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">{`${numberWithCommas(bldcnstsqrft)} sqft`}</Typography>
          </Grid>
        </Grid>
        <Grid container size={{ xs: 12 }} sx={tableRow_}>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">Net</Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">{`${numberWithCommas(netsqrft)} sqft`}</Typography>
          </Grid>
        </Grid>
        <Grid container size={{ xs: 12 }} sx={tableRow_}>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">Net Assignable</Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">{`${numberWithCommas(assignedsqrft)} sqft`}</Typography>
          </Grid>
        </Grid>
        <Grid container size={{ xs: 12 }} sx={tableRow_}>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">Non-Assignable</Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">{`${numberWithCommas(nonassignedsqrft)} sqft`}</Typography>
          </Grid>
        </Grid>
        <Grid container size={{ xs: 12 }} sx={tableRow_}>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">Circulation</Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">{`${numberWithCommas(circsqrft)} sqft`}</Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={0} sx={table_}>
        <Grid container size={{ xs: 12 }} sx={tableRow_}>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">Phase</Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">{bldphase_descrshort}</Typography>
          </Grid>
        </Grid>
        <Grid container size={{ xs: 12 }} sx={tableRow_}>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">Ownership</Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">{bldownership_descr}</Typography>
          </Grid>
        </Grid>
        <Grid container size={{ xs: 12 }} sx={tableRow_}>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">Suitability</Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">{bldsuit_descrshort}</Typography>
          </Grid>
        </Grid>
        <Grid container size={{ xs: 12 }} sx={tableRow_}>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">Address</Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography variant="overline">
              {bldstreetnbr} {bldstreetname}, {bldcity}, {bldstate} {bldpostal}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

// @ref: https://stackoverflow.com/questions/2901102/how-to-print-a-number-with-commas-as-thousands-separators-in-javascript
function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
